import React, { useEffect, useState } from "react";
import api from "@api";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../../../context/AuthContext";
import './TeamAuth.css'

function TeamAuth() {
  const { team_id } = useParams();
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  const [team, setTeam] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (loading) return;

    if (!isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !team_id) return;

    const fetchTeam = async () => {
      try {
        const response = await api.get(`/api/teamsv2/admin/${team_id}`, {
          withCredentials: true,
        });

        setTeam(response.data.team || null);
      } catch (err) {
        console.error("Error fetching team:", err.response?.data || err.message);
        setError("Failed to fetch team");
      }
    };

    fetchTeam();
  }, [isAuthenticated, team_id]);

  const getTeamUsages = () => {
    return (
      team?.seasons_leagues_teams ||
      team?.seasonsLeaguesTeams ||
      team?.team_usages ||
      []
    );
  };

  const getUsageInfo = (usage) => {
    const seasonLeague = usage.seasons_league || usage.seasonsLeague || {};
    const league = seasonLeague.league || usage.league || {};
    const season = seasonLeague.season || usage.season || {};

    return {
      id: usage.id,
      seasonId: season.id || usage.season_id || seasonLeague.season_id,
      leagueId: league.id || usage.league_id || seasonLeague.league_id,
      leagueName: league.name || "Unknown League",
      seasonName: season.name || "Unknown Season",
      captain: usage.captain || "",
      captainId: usage.captain_id || "",
      rosterCount:
        usage.roster_count ??
        usage.rosterCount ??
        usage.rosters?.length ??
        usage.players?.length ??
        0,
    };
  };

  if (error) {
    return (
      <div className="teamIdentity-auth">
        <p className="teamIdentity-error">{error}</p>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="teamIdentity-auth">
        <p className="teamIdentity-loading">Loading team...</p>
      </div>
    );
  }

  const usages = getTeamUsages().map(getUsageInfo);

  return (
    <div className="teamIdentity-auth">
      <div className="teamIdentity-card">


        <div className="teamIdentity-main">
          <h2>{team.name || "Unnamed Team"}</h2>
        </div>
      </div>

      <div className="teamIdentity-section">
        <div className="teamIdentity-section-header">
          <div>
            <h3>Where This Team Is Used</h3>
            <p>
              This shows every season/league connection for this team identity.
            </p>
          </div>

          <div className="teamIdentity-count">
            <span>{usages.length}</span>
            <small>Team Entries</small>
          </div>
        </div>

        <div className="teamIdentity-table-wrap">
          <table className="teamIdentity-table">
            <thead>
              <tr>
                <th>Season</th>
                <th>League</th>
                <th>Captain</th>
                <th>Roster</th>
                <th>Manage</th>
              </tr>
            </thead>

            <tbody>
              {usages.length === 0 ? (
                <tr>
                  <td colSpan="5" className="teamIdentity-empty">
                    This team is not connected to any season yet.
                  </td>
                </tr>
              ) : (
                usages.map((usage, index) => (
                  <tr key={usage.id || index}>
                    <td>{usage.seasonName}</td>
                    <td>{usage.leagueName}</td>
                    <td>
                      {usage.captain ? (
                        <>
                          {usage.captain}
                        </>
                      ) : (
                        <span className="teamIdentity-muted">No captain</span>
                      )}
                    </td>
                    <td>
                      {usage.rosterCount} player
                      {usage.rosterCount === 1 ? "" : "s"}
                    </td>
                    <td>
                      {usage.seasonId && usage.leagueId ? (
                        <NavLink
                          className="teamIdentity-manage-link"
                          to={`/dashboard/seasons/${usage.seasonId}/leagues/${usage.leagueId}/teams/${team.id}`}
                        >
                          Open Roster
                        </NavLink>
                      ) : (
                        <span className="teamIdentity-muted">Missing link</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default TeamAuth;